import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface LRDirectLoadTemplate {
  id: string;
  shipper_user_id: string;
  name: string;
  origin_name: string | null;
  origin_address: string | null;
  origin_city: string | null;
  origin_state: string | null;
  origin_postal_code: string | null;
  origin_country: string | null;
  origin_latitude: number | null;
  origin_longitude: number | null;
  destination_name: string | null;
  destination_address: string | null;
  destination_city: string | null;
  destination_state: string | null;
  destination_postal_code: string | null;
  destination_country: string | null;
  destination_latitude: number | null;
  destination_longitude: number | null;
  equipment_type: string | null;
  weight_lbs: number | null;
  length_ft: number | null;
  load_type: string | null;
  load_nature?: string | null;
  commodity: string | null;
  special_instructions: string | null;
  shipper_notes: string | null;
  handling_requirements: string[];
  appointment_type: string | null;
  priority_tag: string | null;
  post_type: "load" | "quote";
  visibility_mode: "open_market" | "private_only" | "both";
  private_network_id: string | null;
  max_bidders: number | null;
  target_rate_min: number | null;
  target_rate_max: number | null;
  usage_count: number;
  last_used_at: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export function useLRDirectLoadTemplates(userId: string | undefined) {
  return useQuery({
    queryKey: ["lr-direct-load-templates", userId],
    enabled: !!userId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("lr_direct_load_templates" as any)
        .select("*")
        .eq("shipper_user_id", userId!)
        .eq("is_active", true)
        .order("usage_count", { ascending: false })
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as LRDirectLoadTemplate[];
    },
  });
}

export function useLRDirectLoadTemplate(templateId: string | null | undefined) {
  return useQuery({
    queryKey: ["lr-direct-load-template", templateId],
    enabled: !!templateId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("lr_direct_load_templates" as any)
        .select("*")
        .eq("id", templateId!)
        .maybeSingle();
      if (error) throw error;
      return data as unknown as LRDirectLoadTemplate | null;
    },
  });
}

export function useDeleteLRDirectLoadTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("lr_direct_load_templates" as any)
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["lr-direct-load-templates"] });
    },
  });
}

export function useRenameLRDirectLoadTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) => {
      const trimmed = name.trim();
      if (!trimmed) throw new Error("Template name cannot be empty");
      const { data, error } = await supabase
        .from("lr_direct_load_templates" as any)
        .update({ name: trimmed })
        .eq("id", id)
        .select("id")
        .maybeSingle();
      if (error) throw error;
      if (!data) throw new Error("Template not updated");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["lr-direct-load-templates"] });
    },
  });
}

export async function bumpTemplateUsage(id: string) {
  // Fetch current, then increment. Small race is acceptable for a display counter.
  const { data } = await supabase
    .from("lr_direct_load_templates" as any)
    .select("usage_count")
    .eq("id", id)
    .maybeSingle();
  const current = (data as any)?.usage_count ?? 0;
  await supabase
    .from("lr_direct_load_templates" as any)
    .update({ usage_count: current + 1, last_used_at: new Date().toISOString() })
    .eq("id", id);
}
